"use client";

/**
 * Launcher pill — the collapsed-state affordance.
 *
 * Presentational + SDK-free. Mirrors the mindstone.com "Talk to our AI" button:
 * a dark `rounded-full` pill with the coach's face in a circle, a green
 * presence dot, and a visible label. Button has no `rounded-full` pill variant
 * (DSR picker row 3), so this is a custom `<button>`. The dark surface is
 * intentionally fixed (not token-driven) to match the marketing site in both
 * light and dark app themes — see the theming note below.
 *
 * Two `variant`s:
 *  - `floating` (default) — the original free-floating pill: `fixed`
 *    bottom/right 20px + safe-area inset, z-40, 36px avatar, heavy drop
 *    shadow, label always visible.
 *  - `tab` — a right-edge vertical tab that stacks above the app's own
 *    "Chat to us" support tab (UX call, 2026-08-14). It deliberately mirrors
 *    that tab's geometry and interaction — `w-8`, `min-h-32`, `rounded-l-xl`,
 *    `border-r-0`, the `hover:w-9 hover:-translate-x-0.5` nudge — so the two
 *    read as a matched pair rather than two unrelated widgets. The app owns
 *    the stacking and the gap between them; this variant is NOT positioned
 *    (`relative`), it is portalled into the app's rail. See
 *    `launcherSlotSelector` in types.ts.
 *
 * Why the tab is dark rather than the support tab's `quaternary` purple: these
 * two affordances must never be confused — one reaches a human, the other an
 * AI. Matching the geometry makes them siblings; the different surface plus
 * the coach's face keeps them individually identifiable at a glance.
 *
 * Theming: the surface stays `#0e0e10` in BOTH themes rather than inverting in
 * light mode. The tab overlays page content (`--color-background`, `#ffffff`
 * in light), so a light-inverted tab would vanish into the page. A solid dark
 * tab reads as a deliberate control on both themes and keeps the launcher
 * recognisable as the same object as the marketing-site pill.
 *
 * Text direction in `tab` matches the support tab exactly — `[writing-mode:
 * vertical-rl] rotate-180`, so the label reads bottom-to-top. Two adjacent
 * tabs whose labels ran in opposite directions would look broken. The rotation
 * is scoped to the LABEL only: the coach's face must not be turned upside down
 * (the support tab can get away with it because its glyph is near-symmetric).
 *
 * When no `avatarSrc` is supplied it falls back to a Sparkles icon in a muted
 * circle. The "available" affordance is a framer-motion pulse ring behind the
 * avatar (honors `useReducedMotion`). The NPS survey (z-50) may cover the
 * floating variant — accepted per brief; we do not raise z-index.
 */
import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * `floating` = the original fixed bottom-right pill.
 * `tab` = right-edge vertical tab, positioned by the app's rail (not fixed).
 */
export type LauncherVariant = "floating" | "tab";

export interface LauncherButtonProps {
  /** Visible pill label — also the accessible name (from `copy.launcherLabel`). */
  label: string;
  /** Placement/scale. Defaults to `floating` (the pre-top-bar behaviour). */
  variant?: LauncherVariant;
  /** Whether the coach is currently available (drives the pulse ring + presence dot). */
  available?: boolean;
  disabled?: boolean;
  /** Whether the panel is currently expanded (drives `aria-expanded`). */
  expanded?: boolean;
  /** Coach face image; falls back to a Sparkles icon when omitted. */
  avatarSrc?: string;
  onClick: () => void;
  className?: string;
  /** Forwarded to the underlying button (React 19 ref-as-prop). Used by the
   *  shell to restore focus to the launcher after a close transition. */
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * Local motion constants — no motion tokens exist in `global.css` (DSR F5).
 * If the system later adopts `--duration-*`/`--ease-*` tokens, swap these.
 */
const PULSE_DURATION = 2.4;
const PULSE_EASE = "easeOut" as const;

/** Surface + interaction styles shared by both variants (rounding differs). */
const SURFACE =
  "group flex border border-white/10 bg-[#0e0e10] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:pointer-events-none disabled:opacity-60";

const VARIANT_CLASSES: Record<LauncherVariant, string> = {
  floating: cn(
    "fixed bottom-5 right-5 z-40 items-center gap-2.5 rounded-full py-2 pl-2 pr-5 text-sm",
    "shadow-[0_12px_32px_-8px_rgba(10,10,30,0.55)] transition-transform hover:scale-[1.03] active:scale-[0.98] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "[bottom:calc(1.25rem+env(safe-area-inset-bottom))] [right:calc(1.25rem+env(safe-area-inset-right))]",
  ),
  // Geometry + hover nudge deliberately mirror the app's "Chat to us" support
  // tab so the pair reads as one system. `border-r-0` + `rounded-l-xl` make it
  // sit flush against the viewport edge. Not positioned — the app's rail owns
  // placement and the gap between the two tabs.
  tab: cn(
    "relative w-8 min-h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-l-xl border-r-0 px-1 py-3 shadow-md shadow-primary/10",
    "transition-all duration-200 ease-out hover:w-9 hover:-translate-x-0.5 hover:shadow-lg active:translate-x-0",
  ),
};

/** Avatar circle: 36px floating; 24px in the 32px-wide tab. */
const AVATAR_CLASSES: Record<LauncherVariant, string> = {
  floating: "size-9",
  tab: "size-6",
};

/** Presence dot scales with the avatar so the ring weight stays proportional. */
const DOT_CLASSES: Record<LauncherVariant, string> = {
  floating: "-bottom-0.5 -right-0.5 size-2.5",
  tab: "-bottom-0.5 -right-0.5 size-2",
};

export function LauncherButton({
  label,
  variant = "floating",
  available = true,
  disabled = false,
  expanded = false,
  avatarSrc,
  onClick,
  className,
  ref,
}: LauncherButtonProps) {
  const reduceMotion = useReducedMotion();
  // Gate the decorative pulse ring on mount so the launcher's SSR markup and
  // first client render are byte-identical (no framer-motion styles during SSR
  // → hydration invariant holds, Amendment 8). The ring appears only after
  // mount. A launcher entrance animation is intentionally NOT added: framer-
  // motion's `initial` is read only on first mount, so gating it on `mounted`
  // would not trigger the animation, and un-gated `initial` styles would break
  // SSR hydration. The pulse ring is the "alive" affordance.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  const showPulse = mounted && available && !reduceMotion && !disabled && !expanded;

  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-expanded={expanded}
      // Explicit accessible name: in the `tab` variant the visible label is a
      // rotated vertical string, so it should not be relied on as the name.
      aria-label={label}
      className={cn(SURFACE, VARIANT_CLASSES[variant], className)}
    >
      <span className="relative shrink-0">
        {/* Pulse ring — "coach available" affordance. Post-mount only; disabled
            under reduced-motion or while the panel is expanded. */}
        {showPulse ? (
          <motion.span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full bg-white/30"
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: 0, scale: 1.6 }}
            transition={{ duration: PULSE_DURATION, ease: PULSE_EASE, repeat: Infinity }}
          />
        ) : null}
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt=""
            aria-hidden="true"
            className={cn("relative rounded-full object-cover", AVATAR_CLASSES[variant])}
          />
        ) : (
          <span
            className={cn(
              "relative flex items-center justify-center rounded-full bg-white/10",
              AVATAR_CLASSES[variant],
            )}
          >
            <Sparkles
              className={cn("shrink-0", variant === "tab" ? "size-3.5" : "size-5")}
              aria-hidden="true"
            />
          </span>
        )}
        {available ? (
          <span
            aria-hidden="true"
            className={cn(
              "absolute rounded-full border-2 border-[#0e0e10] bg-green-400",
              DOT_CLASSES[variant],
            )}
          />
        ) : null}
      </span>
      {/* `tab`: vertical, reading bottom-to-top — the exact treatment the
          support tab uses, so the two labels run the same way. The rotation is
          confined to this span; the avatar above stays upright. `text-xs` (vs
          the support tab's `text-sm`) keeps the longer translated labels —
          de/fr run ~30% past the English source — inside `min-h-32`. */}
      <span
        className={cn(
          variant === "tab"
            ? "text-xs leading-none tracking-normal [writing-mode:vertical-rl] rotate-180"
            : "whitespace-nowrap",
        )}
      >
        {label}
      </span>
    </motion.button>
  );
}
